import { useState } from 'react';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '@shared/ui';
import { ApiError } from '@shared/api';
import { useAssignCarrierMutation, type ShipmentView } from '@entities/logistics';
import { CarrierSelect } from './carrier-select';

const schema = z.object({
  carrierId: z.string().min(1, 'required'),
  trackingNumber: z.string().trim().max(128).optional().nullable(),
  eta: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')
    .optional()
    .or(z.literal('').transform(() => undefined))
    .nullable(),
});

type AssignFormValues = z.infer<typeof schema>;

interface AssignCarrierDialogProps {
  shipment: ShipmentView;
  orderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AssignCarrierDialog({ shipment, orderId, open, onOpenChange }: AssignCarrierDialogProps) {
  const { t } = useTranslation();
  const mutation = useAssignCarrierMutation(shipment.id, orderId);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<AssignFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      carrierId: shipment.carrier?.id ?? '',
      trackingNumber: shipment.trackingNumber ?? '',
      eta: shipment.etaDate ?? '',
    },
  });

  const onSubmit = (values: AssignFormValues) => {
    setError(null);
    mutation.mutate(
      {
        carrierId: values.carrierId,
        trackingNumber: values.trackingNumber || null,
        eta: values.eta || null,
      },
      {
        onSuccess: () => onOpenChange(false),
        onError: (err) =>
          setError(err instanceof ApiError ? err.message : 'Assignment failed'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('shipment.assign.title')}</DialogTitle>
          <DialogDescription>{t('shipment.assign.description')}</DialogDescription>
        </DialogHeader>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
            <FormField label={t('shipment.assign.carrier')} required error={form.formState.errors.carrierId?.message}>
              {() => (
                <CarrierSelect
                  value={form.watch('carrierId') || null}
                  onChange={(v) =>
                    form.setValue('carrierId', v ?? '', { shouldDirty: true, shouldValidate: true })
                  }
                  fallbackName={shipment.carrier?.name ?? null}
                />
              )}
            </FormField>

            <FormField label={t('shipment.assign.trackingNumber')} error={form.formState.errors.trackingNumber?.message}>
              {(p) => <Input {...p} {...form.register('trackingNumber')} />}
            </FormField>

            <FormField label={t('shipment.assign.eta')} hint={t('shipment.assign.etaHint')} error={form.formState.errors.eta?.message}>
              {(p) => <Input {...p} type="date" {...form.register('eta')} />}
            </FormField>

            {error ? <Alert type="error" showIcon message={error} /> : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" loading={mutation.isPending}>
                {t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
